import React from "react";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { inlinePhotos, photoReport } from "./photos";
import CsaStandardPdf from "./CsaStandardPdf";
import CsaElaboratePdf from "./CsaElaboratePdf";

/**
 * Turn a report model into a file on the auditor's disk.
 *
 * `pdf(doc).toBlob()` then `saveAs`, rather than react-pdf's PDFDownloadLink.
 * The existing HSE exporter arrived at the same conclusion for the same reason
 * — the link's async blob URL is sometimes not ready when the user clicks, and
 * the download silently does nothing. That is a pattern worth reusing; the
 * file it lives in is not opened.
 */

const LAYOUTS = {
  standard: CsaStandardPdf,
  elaborate: CsaElaboratePdf,
};

/** `Acme_Ltd_Plant_1_CSA_2026-08-29.pdf` — sortable, and safe on Windows. */
export function fileNameFor(model, ext) {
  const safe = (s) =>
    String(s || "")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40);

  const date = (model.header.end_date || model.header.start_date || "")
    .toString()
    .slice(0, 10);

  return [safe(model.header.organization), safe(model.header.site), "CSA", date]
    .filter(Boolean)
    .join("_") + "." + ext;
}

/**
 * Render and save.
 *
 * Rendering runs in the browser tab, which is where the auditor already is and
 * where react-pdf is already a dependency. The photo weight that made this
 * risky is handled upstream: the register embeds ~8 KB thumbnails, not the
 * stored images (see CsaStandardPdf).
 */
export async function exportReportPdf(model, opts = {}) {
  const Renderer = LAYOUTS[model.layout] || LAYOUTS.standard;
  if (!LAYOUTS[model.layout]) {
    // A model asking for a layout that does not exist yet still produces a
    // document rather than nothing, but the caller should know it substituted.
    console.warn(`CSA: no renderer for layout "${model.layout}", using standard`);
  }

  // Photographs inlined first: react-pdf cannot authenticate to the API, and
  // reaching S3 from a browser is what failed in the first place.
  const { model: ready, images } = await inlinePhotos(model, opts.photoBytes);

  const blob = await pdf(<Renderer model={ready} />).toBlob();
  const name = opts.fileName || fileNameFor(model, "pdf");
  saveAs(blob, name);
  return { name, bytes: blob.size, photos: photoReport(images) };
}

/** The same document as a blob URL, for an in-page preview. */
export async function previewReportPdf(model, opts = {}) {
  const Renderer = LAYOUTS[model.layout] || LAYOUTS.standard;
  const { model: ready, images } = await inlinePhotos(model, opts.photoBytes);
  const blob = await pdf(<Renderer model={ready} />).toBlob();
  return { url: URL.createObjectURL(blob), bytes: blob.size, photos: photoReport(images) };
}
